/* ─────────────────────────────────────────────────────────────
   Role-based tab access. The sidebar and the ?tab= URL param are
   both filtered through TAB_ACCESS: a user sees the UNION of the
   tabs granted to each of their roles (a user can hold several —
   e.g. astrologer + instructor). `admin` sees everything.

   Role slugs come from the auth BFF (packages/types Role):
   customer (plain user), astrologer, instructor, student, admin.
   ───────────────────────────────────────────────────────────── */

/* NOTE: the plain-user role slug is 'customer' — an upstream 'user' slug is
   normalised to 'customer' (normalizeRoles) before any matching happens, so
   'customer' in a list below covers both spellings. */
const ALL = ['customer', 'user', 'astrologer', 'instructor', 'student'];

/* Sidebar keys → roles allowed to see them (per the final access matrix):
   ┌──────────────┬──────┬────────────┬────────────┬─────────┐
   │ Tab          │ User │ Astrologer │ Instructor │ Student │
   ├──────────────┼──────┼────────────┼────────────┼─────────┤
   │ Dashboard    │  ✓   │     ✓     │     ✓      │    ✓    │
   │ Horoscope    │  ✓   │     ✓     │     ✗      │    ✗    │
   │ Kundli       │  ✓   │     ✓     │     ✗      │    ✗    │
   │ Appoinments  │  ✓   │     ✓     │     ✗      │    ✗    │
   │ Orders       │  ✓   │     ✓     │     ✓      │    ✗    │
   │ Earnings     │  ✗   │     ✓     │     ✓      │    ✗    │
   │ My Articles  │  ✗   │     ✓     │     ✗      │    ✗    │
   │ Academy      │  ✓   │     ✗     │     ✓      │    ✓    │
   │ Reports      │  ✓   │     ✓     │     ✓      │    ✓    │
   │ Remedies     │  ✓   │     ✓     │     ✓      │    ✓    │
   │ My Profile   │  ✓   │     ✓     │     ✓      │    ✓    │
   │ Support      │  ✓   │     ✓     │     ✓      │    ✓    │
   └──────────────┴──────┴────────────┴────────────┴─────────┘
   Keys not listed here are open to everyone. */
export const TAB_ACCESS = {
  overview: ALL,
  horoscope: ['customer', 'user', 'astrologer'],
  kundli: ['customer', 'user', 'astrologer'],
  appoinments: ['customer', 'user', 'astrologer'],
  orders: ['customer', 'user', 'astrologer', 'instructor'],
  // Pooja Energization bookings — seeker-facing only (my-bookings is scoped
  // to the signed-in customer's own bookings).
  pujabookings: ALL,
  earnings: ['astrologer', 'instructor'],
  // Going live is an astrologer's own broadcast; nobody else has one to start.
  golive: ['astrologer'],
  // Writing for the public blog. Astrologer-only for the same reason: the
  // byline is theirs. The API gates it on an approved astrologer PROFILE, not
  // on this role, and an admin still has to approve each article.
  blog: ['astrologer'],
  courses: ['customer', 'user', 'instructor', 'student'],
  reports: ALL,
  remedies: ALL,
  myprofile: ALL,
  support: ALL,
  // Hidden screens (reachable via ?tab= / in-page buttons, not the sidebar)
  // follow the closest sidebar entry's access.
  consultations: ['customer', 'user', 'astrologer'],
  shop: ['customer', 'user', 'astrologer', 'instructor'],
  settings: ALL,
  // "Become an Astrologer" — anyone who isn't one already. The real decision
  // is the admin's (feature flag, evaluated server-side); this only keeps the
  // screen off an astrologer's own dashboard, where it makes no sense.
  becomeastrologer: ['customer', 'user', 'instructor', 'student'],
  // Pricing-slab Settings tab — astrologer-only, and further gated to
  // specific logins via EXCLUSIVE_TABS_BY_EMAIL in DashboardPage.
  pricing: ['astrologer'],
}

/* Highest-privilege-first order used to pick the role that drives a
   tab's default content when the user holds several. */
const ROLE_PRECEDENCE = ['admin', 'astrologer', 'instructor', 'student', 'customer']

/** Auth roles → clean lowercase list; unknown/empty falls back to customer. */
export function normalizeRoles(roles) {
  const list = (Array.isArray(roles) ? roles : [])
    .map((r) => String(r).toLowerCase())
    .map((r) => (r === 'user' ? 'customer' : r))
    .filter((r) => ROLE_PRECEDENCE.includes(r))
  return list.length ? [...new Set(list)] : ['customer']
}

/** The single role that wins when tabs branch content and the user holds several. */
export function primaryRole(roles) {
  const set = new Set(normalizeRoles(roles))
  return ROLE_PRECEDENCE.find((r) => set.has(r)) ?? 'customer'
}

/** True when any of the user's roles grants the tab (admin gets everything). */
export function canAccessTab(tabKey, roles) {
  const allowed = TAB_ACCESS[tabKey]
  if (!allowed) return true
  const rs = normalizeRoles(roles)
  return rs.includes('admin') || rs.some((r) => allowed.includes(r))
}

/** The role that drives ONE tab's content: the highest-precedence role the
 *  user holds AMONG those the tab is granted to — so an astrologer+instructor
 *  gets the instructor Academy screen (astrologer has no Academy access) but
 *  the astrologer Kundli studio. Unrestricted keys and admin accounts fall
 *  back to the overall primary role. */
export function tabRole(tabKey, roles) {
  const allowed = TAB_ACCESS[tabKey]
  const set = new Set(normalizeRoles(roles))
  if (!allowed || set.has('admin')) return primaryRole(roles)
  return ROLE_PRECEDENCE.find((r) => set.has(r) && allowed.includes(r)) ?? primaryRole(roles)
}
